// [Wingman] Auth Sovereignty Layer v2.5.1
import './observability/tracing';
import { validateRouting } from './config/routingValidator';

// Enforce Strict Backend Routing Discipline with Startup Safety Guard
try {
  validateRouting();
} catch (configError) {
  console.error('[Wingman Startup Guard] Routing Validation Failed:', configError);
  // Render fallback diagnostic screen to prevent blank/black screen crashes
  const rootEl = document.getElementById('root');
  if (rootEl) {
    rootEl.innerHTML = `
      <div style="background:#050810; color:#ef4444; height:100vh; width:100vw; display:flex; align-items:center; justify-content:center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <div style="text-align:center; padding:2.5rem; background:rgba(17, 24, 39, 0.95); border:1px solid rgba(239, 68, 68, 0.3); border-radius:16px; max-width:480px; box-shadow:0 25px 50px -12px rgba(0, 0, 0, 0.7);">
          <div style="font-size: 2rem; margin-bottom: 0.5rem;">⚠️</div>
          <h2 style="color:#f87171; font-size:1.25rem; font-weight:600; margin-bottom:0.75rem;">Wingman Startup Guard Notice</h2>
          <p style="color:#d1d5db; font-size:0.9rem; margin-bottom:1rem; line-height:1.5;">${configError.message || 'Routing or environment configuration missing.'}</p>
          <div style="font-size:0.8rem; color:#9ca3af; background:rgba(0,0,0,0.4); padding:0.75rem; border-radius:8px; text-align:left; font-family:monospace;">
            Check your <strong>.env</strong> file for VITE_GATEWAY_URL and ensure the backend service is reachable.
          </div>
        </div>
      </div>
    `;
  }
}

import { dashboardDataAggregator } from './observability/DashboardDataAggregator';
import React, { useState, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client'
// 🗺️ Mapbox GL base stylesheet — REQUIRED for controls, marker positioning,
// and popup/tooltip layout. Without it, mapbox warns
// "This page appears to be missing CSS declarations for Mapbox GL JS" and
// markers / controls render incorrectly.
import 'mapbox-gl/dist/mapbox-gl.css'
import './styles.css'
import { Leva } from 'leva'
import { WingmanHeader } from './ui/WingmanHeader'
import { ErrorBoundary } from './ui/ErrorBoundary'
import { AuthProvider, useAuth } from './auth/useAuth.jsx'
import { InsightBubble } from './components/InsightBubble'
import { BootManager } from './system/BootManager'
import { WorkspaceHost } from './workspaces/WorkspaceHost'

const safeLazy = (loader) =>
  React.lazy(() =>
    loader()
      .catch((err) => {
        console.warn('[Wingman] Dynamic import failed, retrying…', err);
        // Retry once — handles stale Vite HMR timestamps (?t=…)
        return loader();
      })
      .then((mod) => {
        if (!mod.default) {
          console.error('Lazy module missing default export:', mod);
          throw new Error('Lazy module missing default export');
        }
        return mod;
      })
  );

const App = safeLazy(() => import('./App'));
const MultiPurposePanel = safeLazy(() => import('./ui/panels/MultiPurposePanel'));
const MissionControlDashboard = safeLazy(() => import('./ui/dashboard/MissionControlDashboard'));

// MISSION: 10. OUTPUT - Expose dashboard endpoint
// In a real prod environment this would be a backend route, 
// here we expose it for testing/agentic access.
window.getDashboardTelemetry = () => dashboardDataAggregator.getData();
window.onWingmanTrace = (span) => dashboardDataAggregator.pushTrace(span);
console.info("[Wingman] Observability Endpoint Ready: window.getDashboardTelemetry()");


const DashboardRouter = () => {
  const [path] = useState(window.location.pathname);
  
  const LoadingFallback = () => (
    <div style={{
      height: '100vh', 
      width: '100vw', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      background: '#050810'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '2px solid rgba(0, 255, 255, 0.1)',
        borderTop: '2px solid #00ffff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // Basic path detection for Mission Control Dashboard
  if (path === '/dashboard') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <MissionControlDashboard />
      </Suspense>
    );
  }

  const { user } = useAuth();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <BootManager>
        <App />
        {user && <MultiPurposePanel />}
        {user && <InsightBubble />}
        <WorkspaceHost />
      </BootManager>
    </Suspense>
  );
};

createRoot(document.getElementById('root')).render(
  <ErrorBoundary>
    <AuthProvider>
      <DashboardRouter />
      <Leva collapsed hidden={typeof window !== 'undefined' && window.innerWidth < 768} />
    </AuthProvider>
  </ErrorBoundary>
)
